import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { GoldParticles } from "../components/GoldParticles";
import { TRUST_URL } from "../lib/constants";

const LINES = ["I Served So Your Family", "Would Never Lose", "Everything They Built."];

export const Hero = ({ introDone = true }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="https://images.unsplash.com/photo-1606788075819-9574a6edfab3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="A proud Black American family together in a warm home setting"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/55" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/25 to-ink/40" />
      <GoldParticles />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-20 sm:pb-28 pt-40">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-gold text-xs sm:text-sm tracking-[0.32em] uppercase font-semibold mb-8 flex items-center gap-4"
          >
            <span className="h-px w-12 bg-gold" />
            A living trust. A legacy your family deserves. Protection that starts today.
          </motion.p>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl text-cream leading-[1.08] tracking-tight" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={introDone ? { y: 0 } : { y: "115%" }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? (
                    <>
                      Everything They <em className="text-gold not-italic font-medium italic">Built.</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-8 text-cream/80 text-base sm:text-lg leading-relaxed max-w-2xl"
          >
            I am not here to sell you something. Allow me to protect you first. Because of my service, I can
            give you access to tools and protections that wealthy families have used for generations.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6"
          >
            <a
              href={TRUST_URL}
              data-testid="hero-get-started-btn"
              className="group inline-flex items-center justify-center gap-3 bg-gold text-ink font-bold tracking-[0.1em] uppercase text-sm px-9 py-4 rounded-full hover:bg-goldlight transition-colors duration-300 shadow-[0_0_45px_rgba(201,162,60,0.4)] w-fit"
            >
              Get Started
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </a>
            <p className="text-cream/60 text-sm tracking-wide font-medium">
              U.S. Combat Veteran and Founder, Iron Gate Consulting
            </p>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={introDone ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 1.7, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
        aria-hidden="true"
      >
        <div className="w-px h-14 bg-gradient-to-b from-gold/0 via-gold to-gold/0 animate-pulse" />
      </motion.div>
    </section>
  );
};
